// @ts-check
import React from 'react';
import { connect, Dispatch } from 'react-redux'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import formatPhone from 'lib/formatPhone';
import ProfileImage from 'shared/components/ProfileImage';
import GroupStatusIndicator from '../GroupStatusIndicator';
import { showModal } from 'shared/actions/UnboundModalActions';
/* eslint-disable no-unused-vars */
import {
  ProviderMappingResult,
  ReferralProfile,
  Provider,
  Location,
} from '../types';
/* eslint-enable no-unused-vars */
import styles from './ProviderLocationInfoCard.module.scss';
import orgCardStyles from './OrganizationInfoCard.module.scss';
import { DistanceIcon } from 'preferral-ui/components/icons/DistanceIcon';

/** @typedef {{
 distance: number
}} DistanceDisplayProps */

/** @type { React.FC<DistanceDisplayProps> } */
const DistanceDisplay = (props) => {
  const { distance } = props;

  return (
    <div
      className="flex items-center justify-center text-gray-700"
      style={{ fontSize: '1rem' }}
    >
      <DistanceIcon className="h-4 mr-2" />
      {distance.toFixed(1)} mi
    </div>
  );
};

/** @typedef {{
  matchingResult: ProviderMappingResult,
  referralProfile: ReferralProfile,
  onProviderClick: (result: ProviderMappingResult) => void,
  onOrganizationClick: (organizationId: string) => any,
  dispatch: Dispatch<any>
}} ProviderLocationInfoCardPropsType */

/** @type { React.FC<ProviderLocationInfoCardPropsType> } */
const ProviderLocationInfoCard = ({
  matchingResult,
  referralProfile,
  onProviderClick,
  onOrganizationClick,
  dispatch,
}) => {
  const {
    acceptingNewPatients,
    provider,
    location,
    organization,
    earliestAppointmentTime,
    earliestAppointmentTimeInWords,
  } = matchingResult;
  const { address } = location;

  const hasTime = !!earliestAppointmentTime;
  const showDistance =
    !!referralProfile.streetAddress && !!matchingResult.distance;
  const { distance } = matchingResult;

  const showSpecialty = !(
    referralProfile.network &&
    referralProfile.network.hideSpecialtyNameOnMatchingResultCard
  );

  if (organization && organization.onlyAcceptOrganizationReferrals) {
    return (
      <div className={orgCardStyles.container}>
        <div className={orgCardStyles.card_top}>
          <div className={orgCardStyles.card_top__inner}>
            {!!organization.logoUrl ? (
              <img src={organization.logoUrl} alt="logo" />
            ) : (
              <span className={orgCardStyles.heading}>{organization.name}</span>
            )}
            <span className={orgCardStyles.sub_heading}>
              {showSpecialty && provider.specialtyNames.join(', ')}
            </span>
          </div>
        </div>
        <div className={orgCardStyles.card_bottom}>
          <a
            className={orgCardStyles.button}
            onClick={() => onOrganizationClick(organization.id)}
          >
            Refer to Organization
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.MatchingResultInfo}>
      {acceptingNewPatients || (
        <div className={styles.not_accepting}>Not Accepting New Patients</div>
      )}
      <div className={styles.header}>
        <div className={styles.heading}>
          <span
            className="fake-link"
            onClick={() =>
              dispatch(showModal({ name: 'provider', providerId: provider.id }))
            }
          >
            {provider.nameWithAppellation}
          </span>
        </div>
        <div className={styles.sub_heading}>
          {showSpecialty && provider.specialtyNames.join(', ')}
        </div>
      </div>

      <div className={styles.midsection}>
        <div className={styles.midsection__left} />
        <div className={styles.midsection__right}>
          <p className={styles.practice_name}>
            <span
              className="fake-link"
              onClick={() =>
                dispatch(
                  showModal({ name: 'location', locationId: location.id })
                )
              }
            >
              {location.name}
            </span>
          </p>
          <p className={styles.address_line}>
            {address.streetAddress}, {address.streetAddressLine2}
          </p>
          <p className={styles.address_line}>
            {address.city}, {address.state} {address.zip}
          </p>
          <p className={styles.address_line}>
            {location.phone && `P: ${formatPhone(location.phone)}`}{' '}
            {location.fax && `F: ${formatPhone(location.fax)}`}
          </p>
        </div>
      </div>

      {/* Below the others so it appears on top of the previous 2 sections */}
      <div className={styles.provider_image_container}>
        <ProfileImage src={provider.profileImagePath} size={95} />
      </div>

      <div className={styles.footer}>
        <div className={styles.footer__left}>
          <div className="flex gap-2">
            {referralProfile.hasGroup ? (
              <GroupStatusIndicator
                enteredByGuest={!!referralProfile.enteredByGuest}
                enteredByReceiver={!!referralProfile.enteredByReceiver}
                isVhan={!!referralProfile.isVhan}
                hasGroup={referralProfile.hasGroup}
                isInGroup={provider.isInGroup}
                small
              />
            ) : null}
            {showDistance ? (
              <div
                className="absolute"
                style={{ bottom: '1rem', left: '1rem', width: 96 }}
              >
                <DistanceDisplay distance={distance} />
              </div>
            ) : null}
          </div>
          {/* TODO: #1741 Display insurance acceptance information. https://github.com/Preferral/Preferral/issues/1741 */}
          {/* <p className="quick-qualifier">Ins. Accepted</p> */}
        </div>

        <div className={styles.footer__vert} />

        {hasTime ? (
          <div className={styles.footer__right}>
            <div className={styles.footer__stat}>
              {earliestAppointmentTimeInWords}
            </div>
            <div className={styles.footer__label}>Soonest Avail.</div>
          </div>
        ) : (
          <div className={styles.footer__right}>
            <a
              onClick={() => onProviderClick(matchingResult)}
              className="outlined-action-button"
            >
              Send Referral
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

ProviderLocationInfoCard.displayName = 'ProviderLocationInfoCard';

/** @type { React.ValidationMap<ProviderLocationInfoCardPropsType> } */
const propTypes = {
  matchingResult: PropTypes.object.isRequired,
  referralProfile: PropTypes.object.isRequired,
  onProviderClick: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

ProviderLocationInfoCard.propTypes = propTypes;

export default connect()(ProviderLocationInfoCard);
